import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useGetWorkerShift } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { useGetQuestions } from "@src/appV2/redesign/Workplace/Review/api/useGetQuestions";
import { usePostRating } from "@src/appV2/redesign/Workplace/Review/api/usePostRating";
import { usePostReview } from "@src/appV2/redesign/Workplace/Review/api/usePostReview";
import { isAxiosError } from "axios";
import { useCallback, useMemo } from "react";
import { z } from "zod";

import { ReviewQuestionType } from "../redesign/Workplace/Review/types";
import { PostShiftReviewFlowContent } from "./PostShiftReviewFlowContent";
import {
  type NumberQuestionConfig,
  type PostShiftReviewFlowProps,
  type PostShiftReviewFormValues,
  QuestionTag,
} from "./types";

const nestValidationErrorSchema = z.object({ message: z.array(z.string()) });
const OFFENSIVE_WORDS_PATTERN = /should not contain offensive words/i;

export function PostShiftReviewFlow(props: PostShiftReviewFlowProps) {
  const { modalState, facility, onComplete, shiftId, isDelayedReview } = props;

  const { showErrorToast } = useToast();

  // eg: In case user leaves review from review page, then there is no shiftId to tie the review to
  const shouldFetchShiftData = isDefined(shiftId) && isDelayedReview === true;
  // Fetch shift data for delayed review (used for time slot pill)
  const { data: shiftData, isLoading: isShiftLoading } = useGetWorkerShift(shiftId ?? "", {
    enabled: shouldFetchShiftData,
  });

  const {
    data: questionsData,
    isLoading,
    error,
  } = useGetQuestions({
    workplaceType: facility.type,
    workplaceId: facility.id,
  });

  const isError = Boolean(error);

  const submitRatingMutation = usePostRating(facility.id, {
    onSuccess: (response) => {
      logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_RATING_SUBMITTED, {
        workplaceId: facility.id,
        rating: response.data.attributes.rating,
        shiftId,
      });
    },
    onError: () => {
      showErrorToast("Failed to submit rating. Please try again.");
    },
  });

  const submitReviewMutation = usePostReview(facility.id, {
    onSuccess: () => {
      logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_REVIEW_SUBMITTED, {
        workplaceId: facility.id,
        shiftId,
      });
    },
  });

  const questionMappings = useMemo(() => {
    const supportedTags = [
      QuestionTag.SOFTWARE,
      QuestionTag.SUPPLIES,
      QuestionTag.PATIENTS_PER_NURSE,
      QuestionTag.PARKING,
    ];

    const mappings: {
      commentQuestionId: string | undefined;
      tagToQuestionId: Partial<Record<QuestionTag, string>>;
      questionOptions: Record<string, Array<{ id: string; displayText: string }>>;
      questionNumberConfig: Record<string, NumberQuestionConfig>;
      questionTypes: Record<string, ReviewQuestionType>;
    } = {
      commentQuestionId: undefined,
      tagToQuestionId: {},
      questionOptions: {},
      questionNumberConfig: {},
      questionTypes: {},
    };

    if (!questionsData?.data) {
      return mappings;
    }

    const commentQuestion = questionsData.data.find(
      (question) => question.attributes.type === ReviewQuestionType.COMMENT
    );
    if (commentQuestion) {
      mappings.commentQuestionId = commentQuestion.id;
    }

    for (const tag of supportedTags) {
      const matchingQuestion = questionsData.data.find((question) =>
        question.attributes.tags?.some((tagInfo) => tagInfo.tag === tag)
      );

      if (matchingQuestion && !mappings.tagToQuestionId[tag]) {
        mappings.tagToQuestionId[tag] = matchingQuestion.id;
        mappings.questionTypes[matchingQuestion.id] = matchingQuestion.attributes.type;

        if (matchingQuestion.attributes.options?.length > 0) {
          mappings.questionOptions[matchingQuestion.id] = matchingQuestion.attributes.options.map(
            (opt) => ({ id: opt.id, displayText: opt.displayText })
          );
        }

        // Store number config for NUMBER type questions (minValue and maxValue are always defined for NUMBER type)
        if (
          matchingQuestion.attributes.type === ReviewQuestionType.NUMBER &&
          isDefined(matchingQuestion.attributes.minValue) &&
          isDefined(matchingQuestion.attributes.maxValue)
        ) {
          mappings.questionNumberConfig[matchingQuestion.id] = {
            minValue: matchingQuestion.attributes.minValue,
            maxValue: matchingQuestion.attributes.maxValue,
          };
        }
      }
    }

    return mappings;
  }, [questionsData]);

  const handleSubmitRating = useCallback(
    async (rating: number) => {
      await submitRatingMutation.mutateAsync({ rating });
    },
    [submitRatingMutation]
  );

  const createQuestionAnswerWithOptions = useCallback(
    (
      answer: string,
      otherText: string | undefined,
      questionId: string,
      options: Array<{ id: string; displayText: string }>
    ) => {
      const selectedOption = options.find((option) => option.displayText === answer);
      const answerData: { questionId: string; text: string; optionId?: string } = {
        questionId,
        text: answer === "Other" ? (otherText ?? "") : answer,
      };

      if (selectedOption?.id) {
        answerData.optionId = selectedOption.id;
      }

      return answerData;
    },
    []
  );

  const addQuestionAnswers = useCallback(
    (
      formData: PostShiftReviewFormValues,
      answers: Array<{
        questionId: string;
        text: string;
        optionId?: string;
      }>
    ) => {
      if (formData.softwareAnswer && questionMappings.tagToQuestionId[QuestionTag.SOFTWARE]) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.SOFTWARE];
        const options = questionMappings.questionOptions[questionId] || [];
        answers.push(
          createQuestionAnswerWithOptions(
            formData.softwareAnswer,
            formData.softwareOtherText,
            questionId,
            options
          )
        );
      }

      if (formData.suppliesAnswer && questionMappings.tagToQuestionId[QuestionTag.SUPPLIES]) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.SUPPLIES];
        const options = questionMappings.questionOptions[questionId] || [];
        answers.push(
          createQuestionAnswerWithOptions(formData.suppliesAnswer, undefined, questionId, options)
        );
      }

      if (
        formData.patientCountAnswer &&
        questionMappings.tagToQuestionId[QuestionTag.PATIENTS_PER_NURSE]
      ) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.PATIENTS_PER_NURSE];
        const options = questionMappings.questionOptions[questionId] || [];
        answers.push(
          createQuestionAnswerWithOptions(
            formData.patientCountAnswer,
            undefined,
            questionId,
            options
          )
        );
      }

      if (
        formData.parkingAnswer !== undefined &&
        questionMappings.tagToQuestionId[QuestionTag.PARKING]
      ) {
        const questionId = questionMappings.tagToQuestionId[QuestionTag.PARKING];
        answers.push({
          questionId,
          text: formData.parkingAnswer,
        });
      }
    },
    [questionMappings, createQuestionAnswerWithOptions]
  );

  const handleSubmitReview = useCallback(
    async (formData: PostShiftReviewFormValues) => {
      const answers: Array<{ questionId: string; text: string; optionId?: string }> = [];

      if (formData.reviewText.trim() && questionMappings.commentQuestionId) {
        answers.push({
          questionId: questionMappings.commentQuestionId,
          text: formData.reviewText,
        });
      }

      addQuestionAnswers(formData, answers);

      await submitReviewMutation.mutateAsync({
        anonymous: formData.isAnonymous ?? false,
        answers,
      });
    },
    [addQuestionAnswers, questionMappings, submitReviewMutation]
  );

  const onSubmitReviewError = useCallback(
    (error: unknown) => {
      if (isAxiosError(error)) {
        const parsed = nestValidationErrorSchema.safeParse(error.response?.data);
        if (
          parsed.success &&
          parsed.data.message.some((message) => OFFENSIVE_WORDS_PATTERN.test(message))
        ) {
          logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_REVIEW_REJECTED_OFFENSIVE_WORDS, {
            workplaceId: facility.id,
            shiftId,
          });
          showErrorToast(
            "Your review contains inappropriate language. Please revise your review and try again."
          );
          return;
        }
      }

      showErrorToast("Failed to submit review. Please try again.");
    },
    [facility.id, shiftId, showErrorToast]
  );

  const combinedIsLoading = isLoading || (shouldFetchShiftData && isShiftLoading);

  return (
    <PostShiftReviewFlowContent
      modalState={modalState}
      facility={facility}
      shiftId={shiftId}
      questionMappings={questionMappings}
      isLoading={combinedIsLoading}
      isError={isError}
      isDelayedReview={isDelayedReview}
      shift={shiftData?.response}
      onComplete={onComplete}
      onSubmitRating={handleSubmitRating}
      onSubmitReview={handleSubmitReview}
      onSubmitReviewError={onSubmitReviewError}
    />
  );
}
